import { Component, Input, OnInit } from "@angular/core";
import { Articulo } from "../interfaces/articulo.interface";
import { ControlFacturacion } from "../interfaces/control-facturacion.interface";
import {
  CartItem,
  Cotizacion,
} from "../interfaces/CotizacionV2/CotizacionV2.interface";
import { RestService } from "../services/rest.service";
import { PromotionImagesService } from "../services/promotion-images.service";
import {
  fixOrtography,
  currentPromotion,
  isDateInPromotion,
} from "../tools/tools";

/**
 * Banner con los artículos que se muestran debajo de él
 */
interface GrupoBanner {
  imagen: string;
  alt: string;
  items: CartItem[];
}

@Component({
  selector: "app-viewer-cotizacion-v2",
  templateUrl: "./viewer-cotizacion-v2.component.html",
  styleUrls: ["./viewer-cotizacion-v2.component.css"],
})
export class ViewerCotizacionV2Component implements OnInit {
  @Input() cotizacion: Cotizacion;
  @Input() fecha: string; // Fecha de la cotización (viene de preOrder.fecha)
  videoSleccionado = "";

  motoBandera = false;
  cash = false;
  credit = false;
  separated = false;
  openVideo = false;

  // Variables de promoción
  hasActivePromotion = false;
  promocionesCargadas = false; // true cuando ya respondió buscaArticulos para todos los artículos
  gruposContadoApartado: GrupoBanner[] = [];
  gruposCredito: GrupoBanner[] = [];
  promotionLegend = "";
  promotionName: string | null = null;

  constructor(
    public restService: RestService,
    public promotionImages: PromotionImagesService
  ) {}

  ngOnInit() {
    // Inicializar información de promoción
    this.initPromotionInfo();

    this.cotizacion.cartItems.forEach((element) => {
      if (element.saleTypeId === 2) {
        this.cash = true;
      }
      if (element.saleTypeId === 4) {
        this.credit = true;
      }
      if (element.saleTypeId === 6) {
        this.separated = true;
      }
      if (element.line.toLowerCase().includes("motocicleta".toLowerCase())) {
        this.motoBandera = true;
      }
    });

    // Se pinta la cotización hasta saber qué artículos entran en la promoción,
    // para no mostrar primero los banners normales y luego cambiarlos por los de promo
    Promise.all(
      this.cotizacion.cartItems.map(async (element) => {
        element.promocionActiva = await this.getPromocionActiva(element);
        element.otrosEsquemas = await this.getEsquemasArticulo(element).catch(() => []);

        // Log detallado de cada artículo
        // this.logItemPrices(element);
      })
    ).finally(() => {
      this.armarGruposBanners();
      this.promocionesCargadas = true;
      this.logBanners();
    });
  }

  /**
   * Agrupa los artículos por banner para que cada banner salga una sola vez.
   * Por tipo de venta (contado, apartado, crédito), primero los de promoción y luego los normales;
   * en crédito con promoción van primero los artículos y después las motos.
   */
  private armarGruposBanners(): void {
    const articulos = (saleTypeId: number, conPromocion: boolean) =>
      this.cotizacion.cartItems.filter(
        (item) => item.saleTypeId === saleTypeId && this.itemHasPromotion(item) === conPromocion
      );
    const grupo = (
      imageType: 'contado' | 'credito' | 'apartado' | 'motos',
      nombre: string,
      conPromocion: boolean,
      items: CartItem[]
    ): GrupoBanner => ({
      imagen: this.getPromotionImage(imageType, conPromocion),
      alt: `Banner de ${nombre}${conPromocion ? ' - ' + this.promotionName : ''}`,
      items,
    });

    // Con promoción vigente, los artículos de contado y apartado sin promoción van sin banner
    const sinPromocion = (g: GrupoBanner): GrupoBanner =>
      this.hasActivePromotion ? { ...g, imagen: '' } : g;

    this.gruposContadoApartado = [
      grupo('contado', 'contado', true, articulos(2, true)),
      sinPromocion(grupo('contado', 'contado', false, articulos(2, false))),
      grupo('apartado', 'apartado', true, articulos(6, true)),
      sinPromocion(grupo('apartado', 'apartado', false, articulos(6, false))),
    ].filter((g) => g.items.length > 0);

    const creditoConPromocion = articulos(4, true);
    this.gruposCredito = [
      grupo('credito', 'crédito', true, creditoConPromocion.filter((item) => !this.isMotocicleta(item))),
      grupo('motos', 'motos', true, creditoConPromocion.filter((item) => this.isMotocicleta(item))),
      grupo('credito', 'crédito', false, articulos(4, false)),
    ].filter((g) => g.items.length > 0);
  }

  /**
   * Log de lo que realmente se pinta (ya con promocionActiva de cada artículo)
   */
  private logBanners(): void {
    console.log('=== DEBUG BANNERS ===');
    console.log('zone:', this.cotizacion.zone, '| zoneId:', this.cotizacion.zoneId, '| branch:', this.cotizacion.branch, `(${this.cotizacion.branchId})`);
    console.log('hasActivePromotion (fecha):', this.hasActivePromotion, '| promotionName:', this.promotionName);
    console.table(this.cotizacion.cartItems.map(i => ({
      sku: i.sku,
      saleTypeId: i.saleTypeId,
      purchasePlanId: i.purchasePlanId,
      promocionActiva: i.promocionActiva,
      conPromo: this.itemHasPromotion(i),
    })));
    const logGrupo = (g: GrupoBanner) =>
      console.log(`banner ${g.imagen || '(sin banner)'} ->`, g.items.map(i => `${i.sku} (${i.purchasePlanId})`).join(', '));
    this.gruposContadoApartado.forEach(logGrupo);
    if (this.cash || this.separated) console.log('banner envios ->', this.getPromotionImage('envios', this.sectionHasPromotion(2, 6)));
    this.gruposCredito.forEach(logGrupo);
    if (this.credit) console.log('banner pie credito ->', this.getPromotionImage('footerCredit', this.sectionHasPromotion(4)));
    console.log('====================');
  }

  /**
   * Hace log de los precios y planes de un artículo para debugging

  logItemPrices(item: CartItem): void {
    console.log('═══════════════════════════════════════════════════════');
    console.log('📦 ARTÍCULO:', item.sku, '-', item.line);
    console.log('Cantidad:', item.quantity);
    console.log('Tipo de venta:', item.saleTypeId, '(2=Contado, 4=Crédito, 6=Apartado)');
    console.log('Plan de compra ID:', item.purchasePlanId);
    console.log('Plan de compra:', item.purchasePlan);
    console.log('───────────────────────────────────────────────────────');

    item.prices.forEach((price, index) => {
      console.log(`💰 PLAN ${index} (${price.purchasePlan || 'Sin nombre'})`);
      console.log('  - salePrice:', price.salePrice);
      console.log('  - term (meses):', price.term);
      console.log('  - term * 2 (semanas):', price.term * 2);
      console.log('  - deposit (enganche):', price.deposit);
      console.log('  - installationPrice:', price.installationPrice);
      console.log('  - luFactor:', price.luFactor);
      console.log('  - quantity:', price.quantity);

      // Cálculo del pago semanal
      const pagoSemanal = price.luFactor > 0
        ? ((price.salePrice * price.luFactor + price.installationPrice) * price.quantity - price.deposit) / (price.term * 2)
        : ((price.salePrice + price.installationPrice) * price.quantity - price.deposit) / (price.term * 2);

      console.log('  ➜ Pago semanal calculado:', this.roundNumber(pagoSemanal));

      // Precio total del plan
      const precioTotal = price.luFactor > 0
        ? (price.salePrice * price.luFactor + price.installationPrice) * price.quantity
        : (price.salePrice + price.installationPrice) * price.quantity;

      console.log('  ➜ Precio total del plan:', this.roundNumber(precioTotal));

      // Cálculo del ahorro ANTIGUO (incorrecto)
      const ahorroAntiguo = (item.prices[0].salePrice - price.salePrice) * price.quantity;
      console.log('  ➜ Ahorro ANTIGUO (incorrecto):', this.roundNumber(ahorroAntiguo));

      // Cálculo del ahorro NUEVO (correcto)
      const ahorroNuevo = this.calculateSavings(item, price);
      console.log('  ➜ Ahorro NUEVO (correcto):', this.roundNumber(ahorroNuevo));
      console.log('');
    });

    console.log('═══════════════════════════════════════════════════════');
  }
  /**
   * Inicializa la información de la promoción activa
   */
  private initPromotionInfo(): void {
    // console.log('🔍 DEBUG - initPromotionInfo');
    // console.log('currentPromotion.isActive:', currentPromotion.isActive);
    // console.log('this.fecha:', this.fecha);

    if (currentPromotion.isActive && this.fecha) {
      // Parsear fecha en formato DD/MM/YYYY
      const cotizacionDate = this.parseDate(this.fecha);
      console.log('cotizacionDate:', cotizacionDate);
      console.log('currentPromotion.dates:', currentPromotion.dates);

      if (cotizacionDate && !isNaN(cotizacionDate.getTime())) {
        this.hasActivePromotion = isDateInPromotion(cotizacionDate, currentPromotion.dates);
        console.log('hasActivePromotion:', this.hasActivePromotion);

        if (this.hasActivePromotion) {
          this.promotionName = currentPromotion.name;
          this.promotionLegend = currentPromotion.legend;
          // console.log('✅ Promoción activa:', this.promotionName);
        } else {
          // console.log('❌ Cotización fuera del rango de promoción');
        }
      } else {
        console.log('❌ Fecha inválida:', this.fecha);
      }
    } else {
      // console.log('❌ No hay promoción activa o no hay fecha');
    }
  }

  /**
   * Parsea una fecha en formato DD/MM/YYYY a objeto Date
   */
  private parseDate(dateString: string): Date | null {
    if (!dateString) return null;

    // Intentar formato DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Meses en JS son 0-11
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }

    // Si no es ese formato, intentar parsearlo directamente
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  }

  fixOrtography(text: string) {
    return fixOrtography(text);
  }

  onClickOpenVideo(src: string): void {
    this.openVideo = true;
    this.videoSleccionado = src;
  }

  onCloseVideo(): void {
    this.openVideo = false;
    this.videoSleccionado = "";
  }
  tieneEsquemaBuenFin(
    esquemaCotizado: number,
    esquemas: ControlFacturacion[] = []
  ) {
    // console.log("Esquema Cotizado", esquemaCotizado);
    // console.log("Esquemas", esquemas);
    if (esquemaCotizado !== 28) {
      return false;
    }
    for (const esquema of esquemas) {
      if (esquema.control === 39 || esquema.control === 28) {
        return true;
      }
    }
    return false;
  }

  teneApartado(): boolean {
    return this.cotizacion.cartItems.some((item) => item.saleTypeId === 6);
  }

  async getEsquemasArticulo(item: CartItem) {
    const articulo: Articulo = {
      articulo: item.sku,
      esKit: item.kit,
      esLU: item.lu,
      tipoVenta: item.saleTypeId,
      centro: this.cotizacion.zoneId,
      esOnline: false,
    };

    return new Promise<ControlFacturacion[]>((resolve, reject) => {
      this.restService.getConsultaEsquemasArticulo(articulo).subscribe(
        (resp) => {
          // console.log("Articulo", articulo);
          // console.log("Esquemas", resp);
          resolve(resp);
        },
        (err) => {
          // console.log(err);
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta en inventario si el artículo entra en la promoción activa
   */
  async getPromocionActiva(item: CartItem): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.restService
        .buscaArticulo(this.cotizacion.zoneId, this.cotizacion.branchId, item.sku)
        .subscribe((resp) => {
          const articulo = (resp || []).find((a) => a.sku === item.sku);
          resolve(articulo?.promocionActiva === true);
        });
    });
  }

  // Esquemas de Buen Fin por tipo de venta: contado (28 Contado 5% Monedero, 39 Contado Buen Fin)
  // y apartado (29 Apartado Buen Fin). Con otro esquema (p. ej. 31 Contado MSI, 11 Apartado 30
  // Semanas) el artículo no lleva promo aunque esté en la lista de Buen Fin.
  private readonly esquemasPromoContado = [28, 39];
  private readonly esquemasPromoApartado = [29];

  /**
   * El artículo entra en la promoción activa:
   * - Crédito: siempre que la promoción esté vigente, sin importar promocionActiva ni el esquema
   *   (incluye Crédito Dinero Electrónico).
   * - Contado y apartado: promoción vigente, promocionActiva en inventario y cotizado con un
   *   esquema de la promoción.
   */
  itemHasPromotion(item: CartItem): boolean {
    if (item.saleTypeId === 4) {
      return this.hasActivePromotion;
    }
    const esquemasPromo =
      item.saleTypeId === 2 ? this.esquemasPromoContado
      : item.saleTypeId === 6 ? this.esquemasPromoApartado
      : [];
    return (
      this.hasActivePromotion &&
      item.promocionActiva === true &&
      esquemasPromo.includes(item.purchasePlanId)
    );
  }

  getPromotionQr(item: CartItem): string {
    return this.promotionImages.getPromotionQr(
      this.cotizacion.zoneId,
      this.cotizacion.branch,
      this.promotionName
    );
  }

  get isBuenFin(): boolean {
    return this.promotionImages.getPromotionKey(this.promotionName) === 'Buen Fin';
  }

  /**
   * Algún artículo de los tipos de venta indicados entra en la promoción
   */
  sectionHasPromotion(...saleTypeIds: number[]): boolean {
    return this.cotizacion.cartItems.some(
      (item) => saleTypeIds.includes(item.saleTypeId) && this.itemHasPromotion(item)
    );
  }

  /**
   * Obtiene la ruta de la imagen según el tipo y configuración
   */
  getPromotionImage(
    imageType: 'contado' | 'credito' | 'apartado' | 'motos' | 'envios' | 'footerCredit',
    conPromocion: boolean = this.hasActivePromotion
  ): string {
    if (!conPromocion) {
      return this.promotionImages.getImage(imageType, this.cotizacion.zone, this.cotizacion.zoneId, null, this.teneApartado());
    }

    return this.promotionImages.getImage(
      imageType,
      this.cotizacion.zone,
      this.cotizacion.zoneId,
      this.promotionName,
      this.teneApartado()
    );
  }

  isMotocicleta(item: CartItem): boolean {
    return item.line.toLowerCase().includes('motocicleta');
  }

  roundNumber(value: number): number {
    // Redondear hacia arriba o hacia abajo según la parte decimal
    return value - Math.floor(value) < 0.5
      ? Math.floor(value)
      : Math.ceil(value);
  }

  /**
   * Calcula el ahorro correcto comparando precios totales finales
   * @param item El artículo con todos sus planes de pago
   * @param currentPrice El plan de pago actual
   * @returns El ahorro en pesos
   */
  calculateSavings(item: CartItem, currentPrice: any): number {
    // Precio total del plan 0 (referencia - el más caro/largo plazo)
    const precioTotalPlan0 = item.prices[0].luFactor > 0
      ? (item.prices[0].salePrice * item.prices[0].luFactor + item.prices[0].installationPrice) * item.prices[0].quantity
      : (item.prices[0].salePrice + item.prices[0].installationPrice) * item.prices[0].quantity;

    // Precio total del plan actual
    const precioTotalActual = currentPrice.luFactor > 0
      ? (currentPrice.salePrice * currentPrice.luFactor + currentPrice.installationPrice) * currentPrice.quantity
      : (currentPrice.salePrice + currentPrice.installationPrice) * currentPrice.quantity;

    // Ahorro = diferencia de precios totales
    return precioTotalPlan0 - precioTotalActual;
  }

  onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img.src.includes('imagen_no_disponible')) {
      img.src = 'assets/img/imagen_no_disponible.jpg';
    }
  }
}
